import CustomTooltip from '../../shared/CustomTooltip';
import React, { useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    FormHelperText,
    IconButton,
    InputLabel,
    Stack,
    Typography,
    lighten,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AddCircle,
    DragIndicator,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from '@mui/icons-material';
import {
    UseFieldArrayRemove,
    UseFieldArraySwap,
    useFieldArray,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import FormSelect from '../../shared/FormSelect';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import { makeStyles } from '@material-ui/core';
import { SetMenuCourse } from '../../enums/set-menu';
import { ServiceType } from '../../enums/contractor';
import { SetMenuFormValues } from './SetMenuModal';

const COURSE_OPTIONS = [
    { value: SetMenuCourse.Starter, label: 'Starter' },
    { value: SetMenuCourse.Main, label: 'Main' },
    { value: SetMenuCourse.Side, label: 'Side' },
    { value: SetMenuCourse.Dessert, label: 'Dessert' },
    { value: SetMenuCourse.Drink, label: 'Drink' },
];

const Item = ({
    index,
    remove,
    swap,
}: {
    index: number;
    remove: UseFieldArrayRemove;
    swap: UseFieldArraySwap;
}) => {
    const { control } = useFormContext<SetMenuFormValues>();
    const values = useWatch({ control });
    const theme = useTheme();

    return (
        <Card
            variant='outlined'
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
            }}
        >
            <SortableKnob>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1.5,
                        bgcolor: lighten(theme.palette.primary.main, 0.9),
                        cursor: 'grab',
                    }}
                >
                    <DragIndicator color='primary' />
                </Box>
            </SortableKnob>
            <CardContent sx={{ width: '100%' }}>
                <Stack spacing={1.5}>
                    <Box>
                        <InputLabel
                            required
                            htmlFor={`items-${index}-name__input`}
                        >
                            Item Name
                        </InputLabel>
                        <FormTextField
                            id={`items-${index}-name__input`}
                            name={`items[${index}].name`}
                            control={control}
                            placeholder='Grilled prawns'
                        />
                    </Box>
                    <Box>
                        <InputLabel
                            htmlFor={`items-${index}-description__input`}
                        >
                            Description
                        </InputLabel>
                        <FormTextField
                            id={`items-${index}-description__input`}
                            name={`items[${index}].description`}
                            control={control}
                            placeholder='Served with garlic butter and lemon'
                            multiline
                            rows={2}
                        />
                    </Box>
                    <Box>
                        <InputLabel required>Course</InputLabel>
                        <FormSelect
                            name={`items[${index}].course`}
                            control={control}
                            options={COURSE_OPTIONS}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                        }}
                    >
                        {values?.items && values.items.length > 1 && (
                            <>
                                <CustomTooltip title='Move item up'>
                                    <span>
                                        <IconButton
                                            color='default'
                                            disabled={index === 0}
                                            onClick={() =>
                                                swap(index, index - 1)
                                            }
                                        >
                                            <KeyboardArrowUp />
                                        </IconButton>
                                    </span>
                                </CustomTooltip>
                                <CustomTooltip title='Move item down'>
                                    <span>
                                        <IconButton
                                            disabled={
                                                index ===
                                                values.items.length - 1
                                            }
                                            onClick={() =>
                                                swap(index, index + 1)
                                            }
                                        >
                                            <KeyboardArrowDown />
                                        </IconButton>
                                    </span>
                                </CustomTooltip>
                            </>
                        )}
                        <Button
                            color='error'
                            onClick={() => remove(index)}
                            variant='outlined'
                        >
                            Delete
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        gap: 8,
    },
    item: {
        userSelect: 'none',
    },
    dragged: {
        zIndex: 1400,
    },
});

const SetMenuItems = () => {
    const classes = useStyles();
    const addItemButtonRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const {
        control,
        formState: { errors },
    } = useFormContext<SetMenuFormValues>();

    const serviceType = useWatch({ control, name: 'serviceType' });

    const defaultCourse =
        serviceType === ServiceType.Alcohol
            ? SetMenuCourse.Drink
            : SetMenuCourse.Main;

    const { fields, append, remove, move, swap } = useFieldArray({
        name: 'items',
        control,
    });

    const onSortEnd = (a: number, b: number) => {
        move(a, b);
    };

    const appendItemAndScroll = () => {
        append({
            name: '',
            description: '',
            course: defaultCourse,
            order: fields.length,
        });

        requestAnimationFrame(() => {
            addItemButtonRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });
    };

    return (
        <>
            <Stack spacing={1.5}>
                {fields.length === 0 && (
                    <Typography
                        variant='body3'
                        sx={{ color: 'text.secondary', textAlign: 'center' }}
                    >
                        No items added yet.
                    </Typography>
                )}
                <Box>
                    <SortableList
                        onSortEnd={onSortEnd}
                        draggedItemClassName={classes.dragged}
                        className={classes.list}
                    >
                        {fields.map((field, index) => (
                            <SortableItem key={field.id}>
                                <div className={classes.item}>
                                    <Item
                                        index={index}
                                        remove={remove}
                                        swap={swap}
                                    />
                                </div>
                            </SortableItem>
                        ))}
                    </SortableList>
                </Box>
                <Box
                    ref={addItemButtonRef}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    {isMobile || isIpad ? (
                        <IconButton
                            color='primary'
                            size='small'
                            onClick={appendItemAndScroll}
                        >
                            <AddCircle fontSize='small' />
                        </IconButton>
                    ) : (
                        <CustomButton
                            variant='contained'
                            color='primary'
                            onClick={appendItemAndScroll}
                        >
                            Add Item
                        </CustomButton>
                    )}
                </Box>
            </Stack>
            {errors.items && (
                <FormHelperText error>{errors.items.message}</FormHelperText>
            )}
        </>
    );
};

export default SetMenuItems;
